import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import AddIcon from '@material-ui/icons/Add';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import NumberFormat from 'react-number-format';
import MaterialUIDatePicker from '../../components/EyatUIDatePicker';
const bigdecimal = require('bigdecimal');

/**
 * InlineDisclosureTable
 * Kolon-tanimli, tamamen inline duzenlenebilir tablo (modal yok).
 *
 * Props:
 *  - columns: [{ key, label, type, options?, calc?, allowNegative?, minWidth? }]
 *      type: 'date' | 'number' | 'text' | 'select' | 'calc'
 *      - 'select' icin: options: [{ value, label }]
 *      - 'calc'   icin: calc: (row) => number|string   (PASIF/gri, satira yazilir)
 *      - allowNegative: 'number'/'calc' icin negatife izin (net gibi)
 *  - items: object[]                 (kontrollu veri, disari state'te tutulur)
 *  - onChange: (nextItems) => void
 *  - emptyText?, addLabel?
 *
 * Onemli: 'calc' kolonlar dizideki SIRAYLA hesaplanir; bir calc baska bir
 * calc'a bagli olabilir (ornek: gunSonu = gunBasi + net). O yuzden net'i
 * gunSonu'ndan ONCE tanimla.
 */

// Projede kanitlanmis sayi girisi deseni (customInput yerine inputComponent).
function NumberFormatCustom(props) {
    const { inputRef, onChange, ...other } = props;
    return (
        <NumberFormat
            {...other}
            onKeyDown={(e) => { if (e.key === '.') e.preventDefault(); }}
            onValueChange={(values) => {
                if (values.formattedValue === '' || values.formattedValue === ',' || values.formattedValue === '.') {
                    onChange({ target: { name: props.name, value: '' } });
                    return;
                }
                const fv = values.formattedValue !== undefined ? values.formattedValue : String(values.value ?? '');
                const comma = fv.lastIndexOf(',');
                let intPart = (comma === -1 ? fv : fv.slice(0, comma)).replace(/[^\d]/g, '');
                let decPart = comma === -1 ? '' : fv.slice(comma + 1).replace(/[^\d]/g, '');
                if (intPart.length > 20) intPart = intPart.slice(0, 20);
                if (decPart.length > 2) decPart = decPart.slice(0, 2);
                const out = decPart.length ? intPart + '.' + decPart : intPart;
                onChange({ target: { name: props.name, value: out } });
            }}
            thousandSeparator="." decimalSeparator="," decimalScale={2} integerLimit={20} isNumericString allowNegative={false}
        />
    );
}

// Pasif (hesaplanan) hucreler icin tr bicimli gosterim ('.' binlik, ',' ondalik).
// Number/parseFloat kullanilmaz -> buyuk sayilar hassasiyet kaybetmez.
function trFormat(v) {
    if (v === '' || v === null || v === undefined) return '';
    let s = String(v);
    let neg = false;
    if (s.startsWith('-')) { neg = true; s = s.slice(1); }
    let [int, dec] = s.split('.');
    int = (int || '').replace(/\D/g, '');
    dec = (dec || '').replace(/\D/g, '').slice(0, 2);
    if (!int && dec) int = '0';
    int = int.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.');
    let out = int + (dec ? ',' + dec : '');
    if (neg && int.replace(/\./g, '') !== '') out = '-' + out;
    return out;
}

const RED = '#ae3b33';

const useStyles = makeStyles(theme => ({
    wrapper: { width: '100%', overflowX: 'auto' },
    headCell: {
        fontWeight: 700, fontSize: 12.5, color: '#2b2b2e',
        lineHeight: 1.3, verticalAlign: 'bottom', whiteSpace: 'normal',
    },
    cell: { padding: '6px 8px', verticalAlign: 'top' },
    input: { backgroundColor: 'white' },
    calcInput: { backgroundColor: '#f2f2f4' },
    actions: { whiteSpace: 'nowrap' },
    iconBtn: { color: RED, padding: 6 },
    addRow: { padding: '10px 8px' },
    addBtn: {
        backgroundColor: RED, color: 'white', boxShadow: 'none',
        '&:hover': { backgroundColor: '#98322b', boxShadow: 'none' },
    },
    empty: { color: '#8a8a8e', fontStyle: 'italic', padding: '14px 8px' },
    // MUI TextField size="small" yüksekliği ile birebir eşleşir
    datePickerWrapper: {
        // EyatUIDatePicker'in içindeki Grid container'ın yüksekliğini sıfırla
        '& > .MuiGrid-root': {
            padding: 0,
            margin: 0,
            minHeight: 0,
        },
        '& .MuiOutlinedInput-root': {
            height: '100%',
        },
        '& .MuiOutlinedInput-input': {
            padding: '6.5px 0px', // size="small" ile uyumlu padding
            fontSize: '0.75rem',
        },
        '& .MuiInputLabel-root': {
            transform: 'translate(10px, 12px) scale(1)',
        },
        '& .MuiInputLabel-root.MuiInputLabel-shrink': {
            transform: 'translate(10px, -7px) scale(0.75)',
        },
    },
}));

// Bir satirin tum 'calc' kolonlarini SIRAYLA hesaplayip satira yazar.
function recomputeRow(row, columns) {
    const next = { ...row };
    columns.forEach((col) => {
        if (col.type === 'calc' && typeof col.calc === 'function') {
            const val = col.calc(next);
            next[col.key] = (val === null || val === undefined || Number.isNaN(val)) ? '' : String(val);
        }
    });
    return next;
}

// Bos satir uretir (calc kolonlar da hesaplanir).
export function makeEmptyRow(columns) {
    const row = {};
    columns.forEach((col) => { row[col.key] = col.type === 'date' ? null : ''; });
    return recomputeRow(row, columns);
}

// Submit oncesi tam dolu mu kontrolu (calc kolonlar haric).
export function areRowsComplete(items, columns) {
    if (!items || items.length === 0) return false;
    return items.every((row) =>
        columns.every((col) => {
            if (col.type === 'calc') return true;
            const v = row[col.key];
            return v !== null && v !== undefined && String(v).trim() !== '';
        })
    );
}

export default function InlineDisclosureTable(props) {
    const classes = useStyles();
    const { columns, items, onChange } = props;
    const emptyText = props.emptyText || 'Tabloda kayıt bulunmamaktadır.';
    const addLabel = props.addLabel || 'Yeni satır ekle';

    const updateCell = (rowIndex, key, value) => {
        const next = items.map((row, i) =>
            i === rowIndex ? recomputeRow({ ...row, [key]: value }, columns) : row
        );
        onChange(next);
    };

    const addRow = () => onChange([...(items || []), makeEmptyRow(columns)]);
    const removeRow = (rowIndex) => onChange(items.filter((_, i) => i !== rowIndex));

    const renderCell = (row, rowIndex, col) => {
        const value = row[col.key];

        if (col.type === 'calc') {
            // PASIF (non-editable), gri gosterim; deger satira zaten yazilmis durumda
            return (
                <TextField
                    disabled fullWidth size="small" variant="outlined" className={classes.calcInput}
                    value={trFormat(value)}
                />
            );
        }

        if (col.type === 'date') {
            return (
                <MaterialUIDatePicker
                    noMargin
                    disableFuture
                    size="small"
                    containerId={'inline-date-' + rowIndex + '-' + col.key}
                    dateString={value}
                    setContainerSelectedDate={(d) => updateCell(rowIndex, col.key, d)}
                />
            );
        }

        if (col.type === 'select') {
            return (
                <TextField
                    select fullWidth size="small" variant="outlined" className={classes.input}
                    value={value || ''}
                    onChange={(e) => updateCell(rowIndex, col.key, e.target.value)}
                >
                    {(col.options || []).map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                </TextField>
            );
        }

        if (col.type === 'number') {
            return (
                <TextField
                    fullWidth size="small" variant="outlined" className={classes.input}
                    name={col.key}
                    value={value === null || value === undefined ? '' : value}
                    onChange={(e) => updateCell(rowIndex, col.key, e.target.value)}
                    InputProps={{ inputComponent: NumberFormatCustom }}
                />
            );
        }

        // 'text'
        return (
            <TextField
                fullWidth size="small" variant="outlined" className={classes.input}
                value={value || ''}
                onChange={(e) => updateCell(rowIndex, col.key, e.target.value)}
            />
        );
    };

    const colSpan = columns.length + 1;

    return (
        <div className={classes.wrapper}>
            <Table size="small" aria-label="inline-disclosure-table">
                <TableHead>
                    <TableRow>
                        {columns.map((col) => (
                            <TableCell
                                key={col.key}
                                align={col.type === 'number' || col.type === 'calc' ? 'right' : 'left'}
                                className={classes.headCell}
                                style={col.minWidth ? { minWidth: col.minWidth } : undefined}
                            >
                                {col.label}
                            </TableCell>
                        ))}
                        <TableCell className={classes.headCell} align="center" style={{ width: 92 }}>İşlem</TableCell>
                    </TableRow>
                </TableHead>

                <TableBody>
                    {(items || []).map((row, rowIndex) => (
                        <TableRow key={'row-' + rowIndex} hover>
                            {columns.map((col) => (
                                <TableCell
                                    key={col.key}
                                    className={classes.cell}
                                    style={col.minWidth ? { minWidth: col.minWidth } : undefined}
                                >
                                    {renderCell(row, rowIndex, col)}
                                </TableCell>
                            ))}
                            <TableCell align="center" className={classes.cell + ' ' + classes.actions}>
                                <IconButton
                                    size="small" className={classes.iconBtn} aria-label="sil"
                                    onClick={() => removeRow(rowIndex)}
                                >
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                            </TableCell>
                        </TableRow>
                    ))}

                    {(!items || items.length === 0) && (
                        <TableRow>
                            <TableCell colSpan={colSpan} className={classes.empty}>{emptyText}</TableCell>
                        </TableRow>
                    )}

                    <TableRow>
                        <TableCell colSpan={colSpan} className={classes.addRow}>
                            <Button variant="contained" className={classes.addBtn} onClick={addRow}>
                                <AddIcon style={{ marginRight: 8 }} />
                                {addLabel}
                            </Button>
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </div>
    );
}

// -------------------------------------------------------------------------
// HAZIR KOLON KONFIGURASYONLARI
// Bizyuk sayilarda JS hassasiyetini kaybetmemesi icin bigdecimal kullanilir.
const toBD = (v) => {
    if (v === null || v === undefined || v === '') return new bigdecimal.BigDecimal('0');
    let s = String(v).trim();
    if (!s) return new bigdecimal.BigDecimal('0');
    const neg = s.startsWith('-');
    let body = neg ? s.slice(1) : s;
    // Value state'te ASCII nokta-onalklidir; saglik icin ayrac temizle.
    body = body.replace(/[^\d.]/g, '');
    if (body === '') body = '0';
    try { return new bigdecimal.BigDecimal((neg ? '-' : '') + body); }
    catch { return new bigdecimal.BigDecimal('0'); }
};
const num = (v) => toBD(v).toPlainString();
const bdSub = (a, b) => toBD(a).subtract(toBD(b)).toPlainString();
const bdAdd = (a, b) => toBD(a).add(toBD(b)).toPlainString();
const bdMul = (a, b) => toBD(a).multiply(toBD(b)).toPlainString();

// PaySenetleri tablosu
// Onemli sira: netNominalTutar, gunSonuNominalTutar'dan ONCE (gunSonu = gunBasi + net)
export const PAY_SENETLERI_COLUMNS = [
    { key: 'islemTarihi', label: 'İşlemin Tarihi', type: 'date', minWidth: 130 },
    { key: 'alisNominalTutar', label: 'Alım İşlemine Konu Payların Toplam Nominal Tutarı (TL)', type: 'number', minWidth: 150 },
    { key: 'satisNominalTutar', label: 'Satım İşlemine Konu Payların Toplam Nominal Tutarı (TL)', type: 'number', minWidth: 150 },
    { key: 'netNominalTutar', label: 'İşlemlerin Net Nominal Tutarı * (TL)', type: 'calc', allowNegative: true,
      calc: (r) => bdSub(r.alisNominalTutar, r.satisNominalTutar), minWidth: 130 },
    { key: 'gunBasiNominalTutar', label: 'Sahip Olunan Payların Gün Başı Nominal Tutarı (TL)', type: 'number', minWidth: 150 },
    { key: 'gunSonuNominalTutar', label: 'Sahip Olunan Payların Gün Sonu Nominal Tutarı (TL)', type: 'calc', allowNegative: true,
      calc: (r) => bdAdd(r.gunBasiNominalTutar, r.netNominalTutar), minWidth: 150 },
    { key: 'gunBasiBakiyesininSermayeyeOrani', label: 'Sahip Olunan Payların Gün Başı Bakiyesinin Sermayeye Oranı (%)', type: 'number', minWidth: 160 },
    { key: 'gunBasiBakiyesininOyHaklarinaOrani', label: 'Sahip Olunan Oy Haklarının Gün Başı Bakiyesinin Oy Haklarına Oranı (%)', type: 'number', minWidth: 170 },
    { key: 'gunSonuBakiyesininSermayeyeOrani', label: 'Sahip Olunan Payların Gün Sonu Bakiyesinin Sermayeye Oranı (%)', type: 'number', minWidth: 160 },
    { key: 'gunSonuBakiyesininOyHaklarinaOrani', label: 'Sahip Olunan Oy Haklarının Gün Sonu Bakiyesinin Oy Haklarına Oranı (%)', type: 'number', minWidth: 170 },
];

// PayDisi tablosu (islemTutari = Adet x Fiyat -> calc/pasif)
export const PAY_DISI_COLUMNS = [
    { key: 'islemTarihi', label: 'İşlem Tarihi', type: 'date', minWidth: 130 },
    { key: 'islemNiteligi', label: 'İşlem Niteliği', type: 'select',
      options: [{ value: 'Alım', label: 'Alım' }, { value: 'Satım', label: 'Satım' }], minWidth: 120 },
    { key: 'isinCode', label: 'Sermaye Piyasası Aracının ISIN Kodu', type: 'text', minWidth: 160 },
    { key: 'nominalTutar', label: 'İşleme Konu Sermaye Piyasası Aracının Nominal Tutarı (TL)', type: 'number', minWidth: 160 },
    { key: 'sermayePiyasasiAdedi', label: 'İşleme Konu Sermaye Piyasası Aracı Adedi', type: 'number', minWidth: 150 },
    { key: 'islemFiyati', label: 'İşlem Fiyatı (TL)', type: 'number', minWidth: 120 },
    { key: 'islemTutari', label: 'İşlem Tutarı (TL)', type: 'calc',
      calc: (r) => bdMul(r.sermayePiyasasiAdedi, r.islemFiyati), minWidth: 120 },
    { key: 'islemdenOnceNominalTutar', label: 'İşlem Öncesi Sahip Olunan Nominal Tutar (TL)', type: 'number', minWidth: 150 },
    { key: 'islemdenSonraNominalTutar', label: 'İşlem Sonrası Sahip Olunan Nominal Tutar (TL)', type: 'number', minWidth: 150 },
];